import {
    Box,
    Typography,
    Button,
    TextField,
    styled,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    Stack,
    CircularProgress,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Card,
    CardContent,
    Fade,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect } from 'react';
import { keyframes } from '@mui/material/styles';

import { ROUTES_URL } from 'src/constants/routes';
import useGoTo from 'src/hooks/useGoTo';
import useProjectsStore from 'src/stores/projects';
import { LayoutPaper, DesktopLayout } from 'src/layout';
import { useLocation } from 'react-router-dom';
import ProjectsService from 'src/services/ProjectsService';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import Title from 'src/components/Title';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import { AIIcon } from 'src/icons/AIIcon';
import NicheAnalysisService, {
    type INicheAnalysisResponse,
    type IRecommendedCollection,
    type ILeadExample,
} from 'src/services/NicheAnalysisService';
import MapLayer from 'src/icons/MapLayer';
import Peoples from 'src/icons/Peoples';
import ProjectService from 'src/services/ProjectService';
import CollectionService from 'src/services/CollectionService';
import { useQuery, useQueryClient } from 'react-query';
import { getWordWithNumber } from 'src/utils';

const ProjectSchema = z.object({
    name: z.string().min(1, 'Введите название проекта').max(100, 'Не более 100 символов'),
    project_goal: z.enum(['attract_clients', 'attract_telegram'], {
        required_error: 'Выберите цель проекта',
    }),
    audience_description: z.string().min(1, 'Опишите ваш продукт или услугу').max(150, 'Не более 150 символов'),
});

const CustomTextField = styled(TextField)(({ theme }) => ({
    '&.MuiTextField-root': { borderRadius: '16px' },
    backgroundColor: '#fff',
    '& .MuiInputBase-root:not(.Mui-focused) .MuiInputBase-input:not(:placeholder-shown)': {
        color: theme.palette.primary.main,
        opacity: 1,
    },
    '& .MuiOutlinedInput-root': {
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000000',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000000',
        },
    },
}));

const pulseAnimation = keyframes`
  0% { 
    box-shadow: 0 0 0 0 rgba(122, 48, 228, 0.4), 0 0 0 0 rgba(255, 136, 192, 0.4);
    transform: scale(1);
  }
  50% { 
    box-shadow: 0 0 0 20px rgba(122, 48, 228, 0), 0 0 0 30px rgba(255, 136, 192, 0);
    transform: scale(1.05);
  }
  100% { 
    box-shadow: 0 0 0 0 rgba(122, 48, 228, 0), 0 0 0 0 rgba(255, 136, 192, 0);
    transform: scale(1);
  }
`;

const floatAnimation = keyframes`
  0%, 100% { 
    transform: translateY(0px);
  }
  50% { 
    transform: translateY(-10px);
  }
`;

const dotsAnimation = keyframes`
  0%, 20% { opacity: 0; }
  40% { opacity: 1; }
  60%, 100% { opacity: 0; }
`;

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const MaxChars = ({ isError, charCount, maxChars }: { isError: boolean; charCount: number; maxChars: number }) => {
    return (
        <Stack
            gap="2px"
            direction="row"
            alignItems="center"
            sx={{ mt: 0.5 }}
        >
            {!isError && <AIIcon />}

            <Typography
                variant="body2"
                sx={{
                    background: isError ? 'none' : 'linear-gradient(90deg, #FF88C0, #9FA9BC)',
                    WebkitBackgroundClip: isError ? 'none' : 'text',
                    color: isError ? 'error.main' : 'transparent',
                }}
            >
                {isError ? `Превышено количество символов (максимум ${maxChars})` : `${charCount}/${maxChars}`}
            </Typography>
        </Stack>
    );
};

export default function AboutProject() {
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const goTo = useGoTo();
    const isMobile = useIsMobileSize();
    const [analysisResult, setAnalysisResult] = useState<INicheAnalysisResponse | null>(null);
    const [selectedLeads, setSelectedLeads] = useState<Set<number>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const { createProject, activeProject, updateProject, setProjects, setActiveProjectById, projects } =
        useProjectsStore(
            ({ createProject, activeProject, updateProject, setProjects, setActiveProjectById, projects }) => ({
                createProject,
                activeProject,
                updateProject,
                setProjects,
                setActiveProjectById,
                projects,
            })
        );

    // Читаем projectId из URL параметров
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const projectIdParam = searchParams.get('projectId');
        if (projectIdParam) {
            const projectId = parseInt(projectIdParam, 10);
            if (!isNaN(projectId)) {
                // Проверяем, есть ли проект в списке проектов
                const project = projects.find((p) => p.id === projectId);
                if (project) {
                    setActiveProjectById(projectId);
                } else {
                    // Если проекта нет в списке, загружаем проекты
                    useProjectsStore.getState().setProjects([]);
                    ProjectsService.getProjects()
                        .then((data) => {
                            useProjectsStore.getState().setProjects(data);
                            setActiveProjectById(projectId);
                        })
                        .catch(() => {
                            enqueueSnackbar('Ошибка загрузки проекта', { variant: 'error' });
                        });
                }
            }
        }
    }, [location.search, projects, setActiveProjectById, enqueueSnackbar]);

    // Загружаем сохраненные результаты анализа ниши при монтировании
    const hasStoredAnalysis =
        activeProject?.niche_analysis_keywords && activeProject.niche_analysis_keywords.length > 0;

    const { data: recommendedCollectionsData, isLoading: isLoadingCollections } = useQuery(
        ['project-recommended-collections', activeProject?.id],
        () => ProjectService.getProjectRecommendedCollections(activeProject!.id!),
        {
            enabled: !!(
                hasStoredAnalysis &&
                !!activeProject?.id &&
                !!activeProject?.recommended_collections &&
                activeProject.recommended_collections.length > 0
            ),
            onError: (error) => {
                console.error('Ошибка загрузки рекомендованных подборок:', error);
            },
        }
    );

    const { data: foundLeadsData, isLoading: isLoadingFoundLeads } = useQuery(
        ['project-found-leads', activeProject?.id],
        () => ProjectService.getProjectFoundLeads(activeProject!.id!),
        {
            enabled: !!(hasStoredAnalysis && !!activeProject?.id),
            onError: (error) => {
                console.error('Ошибка загрузки найденных лидов:', error);
            },
        }
    );

    const { data: selectedLeadsData, isLoading: isLoadingSelectedLeads } = useQuery(
        ['project-selected-leads', activeProject?.id],
        () => ProjectService.getProjectSelectedLeads(activeProject!.id!),
        {
            enabled: !!(hasStoredAnalysis && !!activeProject?.id),
            // Всегда запрашиваем, чтобы получить актуальное состояние (даже если пустой массив)
            refetchOnMount: true,
            refetchOnWindowFocus: true,
            onError: (error) => {
                console.error('Ошибка загрузки выбранных лидов:', error);
            },
        }
    );

    // Загружаем детали подборок для получения chat_count (только если нужно)
    const collectionIds = recommendedCollectionsData?.recommended_collections?.map((col: any) => col.id) || [];
    const needsCollectionDetails = hasStoredAnalysis && collectionIds.length > 0;
    const { data: publicCollectionsData, isLoading: isLoadingCollectionDetails } = useQuery(
        ['public-collections-for-details', collectionIds.join(',')],
        () => CollectionService.getPublicCollections(),
        {
            enabled: !!needsCollectionDetails,
            onError: (error) => {
                console.error('Ошибка загрузки подборок:', error);
            },
        }
    );

    // Формируем результат анализа из сохраненных данных
    useEffect(() => {
        if (hasStoredAnalysis && activeProject) {
            // Ждем загрузки данных только если они нужны
            const needsCollections =
                activeProject.recommended_collections && activeProject.recommended_collections.length > 0;
            const needsLeads =
                activeProject.found_leads || (activeProject.selected_leads && activeProject.selected_leads.length > 0);

            if (needsCollections && (isLoadingCollections || isLoadingCollectionDetails)) return;
            if (needsLeads && (isLoadingFoundLeads || isLoadingSelectedLeads)) return;

            const collections: IRecommendedCollection[] = [];

            if (recommendedCollectionsData?.recommended_collections) {
                recommendedCollectionsData.recommended_collections.forEach((col: any) => {
                    // Ищем полную информацию о подборке для получения chat_count
                    const fullCollection = publicCollectionsData?.chat_bundles?.find(
                        (bundle: any) => bundle.id === col.id
                    );

                    collections.push({
                        id: col.id,
                        name: col.name,
                        city: col.location__name || null,
                        tags: col.tags || [],
                        chat_count: fullCollection?.chat_count || 0,
                    });
                });
            }

            const leadExamples: ILeadExample[] = [];
            // Используем найденные лиды для отображения (если есть)
            if (foundLeadsData?.found_leads && foundLeadsData.found_leads.length > 0) {
                foundLeadsData.found_leads.forEach((lead: any) => {
                    leadExamples.push({
                        id: lead.id,
                        text: lead.text || '',
                        source: lead.source || '',
                        date: lead.date || '',
                        category: lead.category || '',
                        category_icon: lead.category_icon || undefined,
                        keywords: lead.keywords || [],
                    });
                });
            } else if (selectedLeadsData?.selected_leads) {
                // Fallback на selected_leads для обратной совместимости
                selectedLeadsData.selected_leads.forEach((lead: any) => {
                    leadExamples.push({
                        id: lead.id,
                        text: lead.message__text || '',
                        source: lead.message__source || '',
                        date: lead.message__date || '',
                        category: lead.category || '',
                        category_icon: lead.category_icon || undefined,
                        keywords: lead.message__keywords || [],
                    });
                });
            }

            const result: INicheAnalysisResponse = {
                keywords: activeProject.niche_analysis_keywords || [],
                lead_examples: leadExamples,
                recommended_collections: {
                    city_collections: collections.filter((col) => col.city),
                    general_collections: collections.filter((col) => !col.city),
                },
                analysis_summary: activeProject.niche_analysis_summary || '',
                category: activeProject.niche_analysis_category || undefined,
                project_id: activeProject.id!,
            };

            setAnalysisResult(result);

            // Устанавливаем уже выбранные лиды (если есть), иначе очищаем
            if (selectedLeadsData?.selected_leads && selectedLeadsData.selected_leads.length > 0) {
                const selectedIds = new Set(selectedLeadsData.selected_leads.map((lead: any) => lead.id));
                setSelectedLeads(selectedIds);
            } else {
                // Если нет сохраненных выбранных лидов, очищаем выбор
                setSelectedLeads(new Set());
            }
        }
    }, [
        hasStoredAnalysis,
        activeProject,
        recommendedCollectionsData,
        foundLeadsData,
        selectedLeadsData,
        publicCollectionsData,
        isLoadingCollections,
        isLoadingCollectionDetails,
        isLoadingFoundLeads,
        isLoadingSelectedLeads,
    ]);

    // Отдельный useEffect для обновления выбранных лидов при изменении selectedLeadsData
    useEffect(() => {
        if (selectedLeadsData !== undefined) {
            if (selectedLeadsData?.selected_leads && selectedLeadsData.selected_leads.length > 0) {
                const selectedIds = new Set(selectedLeadsData.selected_leads.map((lead: any) => lead.id));
                setSelectedLeads(selectedIds);
            } else {
                // Если нет сохраненных выбранных лидов, очищаем выбор
                setSelectedLeads(new Set());
            }
        }
    }, [selectedLeadsData]);

    const {
        control,
        handleSubmit,
        formState: { isValid, errors },
        watch,
        reset,
    } = useForm({
        defaultValues: {
            name: activeProject?.name || '',
            project_goal: (activeProject?.project_goal as 'attract_clients' | 'attract_telegram') || 'attract_clients',
            audience_description: activeProject?.audience_description || '',
        },
        resolver: zodResolver(ProjectSchema),
        mode: 'onChange',
    });

    // Обновляем форму при изменении activeProject
    useEffect(() => {
        if (activeProject) {
            reset({
                name: activeProject.name || '',
                project_goal:
                    (activeProject.project_goal as 'attract_clients' | 'attract_telegram') || 'attract_clients',
                audience_description: activeProject.audience_description || '',
            });
        }
    }, [activeProject, reset]);

    const { mutate: createOrUpdateProject, isLoading: isCreatingProject } = useMutation(
        async (data: {
            name: string;
            project_goal: 'attract_clients' | 'attract_telegram';
            audience_description: string;
        }) => {
            const currentActiveProject = useProjectsStore.getState().activeProject;
            let projectId: number;

            if (currentActiveProject?.id) {
                // Если проект уже существует, обновляем его
                await updateProject({
                    id: currentActiveProject.id,
                    name: data.name,
                    project_goal: data.project_goal,
                    audience_description: data.audience_description,
                });
                projectId = currentActiveProject.id;

                // Обновляем activeProject с новыми данными
                const { setActiveProject } = useProjectsStore.getState();
                setActiveProject({
                    ...currentActiveProject,
                    name: data.name,
                    project_goal: data.project_goal,
                    audience_description: data.audience_description,
                });
            } else {
                // Создаем новый проект (не должно происходить, так как проект уже создан)
                const newProject = await createProject({
                    name: data.name,
                    project_goal: data.project_goal,
                    audience_description: data.audience_description,
                });
                projectId = newProject.id;
            }

            return projectId;
        },
        {
            onError: (error: any) => {
                console.error('Ошибка создания проекта:', error);
                enqueueSnackbar(
                    error.response?.data?.error || 'Ошибка создания проекта. Попробуйте повторить позднее',
                    { variant: 'error' }
                );
            },
        }
    );

    const { mutate: analyzeNiche, isLoading: isAnalyzing } = useMutation(
        async (data: {
            project_id: number;
            service_description: string;
            project_goal: 'attract_clients' | 'attract_telegram';
        }) => {
            return await NicheAnalysisService.analyzeNiche({
                project_id: data.project_id,
                service_description: data.service_description,
                project_goal: data.project_goal,
            });
        },
        {
            onSuccess: (result: INicheAnalysisResponse) => {
                setAnalysisResult(result);
                // Очищаем выбранные лиды после нового анализа ниши
                setSelectedLeads(new Set());
                enqueueSnackbar('Анализ ниши выполнен успешно', { variant: 'success' });
            },
            onError: (error: any) => {
                console.error('Ошибка анализа ниши:', error);
                enqueueSnackbar(error.response?.data?.error || 'Ошибка анализа ниши. Попробуйте повторить позднее', {
                    variant: 'error',
                });
            },
        }
    );

    const handleNext = handleSubmit(async (data) => {
        setIsSaving(true);
        const projectData = {
            name: data.name.trim(),
            project_goal: data.project_goal,
            audience_description: data.audience_description.trim(),
        };

        // Сначала создаем/обновляем проект
        try {
            const currentActiveProject = useProjectsStore.getState().activeProject;
            let projectId: number;

            if (currentActiveProject?.id) {
                // Если проект уже существует, обновляем его
                await updateProject({
                    id: currentActiveProject.id,
                    name: projectData.name,
                    project_goal: projectData.project_goal,
                    audience_description: projectData.audience_description,
                });
                projectId = currentActiveProject.id;

                // Обновляем activeProject с новыми данными
                const { setActiveProject } = useProjectsStore.getState();
                setActiveProject({
                    ...currentActiveProject,
                    name: projectData.name,
                    project_goal: projectData.project_goal,
                    audience_description: projectData.audience_description,
                });
            } else {
                // Создаем новый проект (не должно происходить, так как проект уже создан)
                const newProject = await createProject({
                    name: projectData.name,
                    project_goal: projectData.project_goal,
                    audience_description: projectData.audience_description,
                });
                projectId = newProject.id;
            }

            // Сохраняем выбранные лиды, если есть активный проект
            if (projectId && selectedLeads.size >= 0) {
                try {
                    await ProjectService.saveSelectedLeads({
                        project_id: projectId,
                        selected_lead_ids: Array.from(selectedLeads),
                    });
                    if (selectedLeads.size > 0) {
                        enqueueSnackbar('Выбранные лиды сохранены', { variant: 'success' });
                    }
                } catch (error: any) {
                    console.error('Ошибка сохранения выбранных лидов:', error);
                    enqueueSnackbar(error.response?.data?.error || 'Ошибка сохранения выбранных лидов', {
                        variant: 'error',
                    });
                }
            }

            enqueueSnackbar('Проект успешно сохранен', { variant: 'success' });

            // Возвращаемся на страницу проектов
            goTo(ROUTES_URL.PROJECTS);
        } catch (error: any) {
            console.error('Ошибка сохранения проекта:', error);
            enqueueSnackbar(error.response?.data?.error || 'Ошибка сохранения проекта. Попробуйте повторить позднее', {
                variant: 'error',
            });
        } finally {
            setIsSaving(false);
        }
    });

    const onSubmit = async (data: z.infer<typeof ProjectSchema>) => {
        const projectData = {
            name: data.name.trim(),
            project_goal: data.project_goal,
            audience_description: data.audience_description.trim(),
        };

        // Сначала создаем/обновляем проект
        createOrUpdateProject(projectData, {
            onSuccess: (projectId: number) => {
                // После успешного создания проекта запускаем анализ ниши
                analyzeNiche({
                    project_id: projectId,
                    service_description: projectData.audience_description,
                    project_goal: projectData.project_goal,
                });
            },
        });
    };

    const isLoading = isAnalyzing;

    const renderContent = () => {
        return (
            <LoadingWrapper isLoading={false}>
                <Box
                    pt={2}
                    minHeight="100%"
                    display="flex"
                    flexDirection="column"
                >
                    <Box mb={1.5}>
                        <Title>О проекте</Title>
                    </Box>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            <Stack gap={1}>
                                <Typography
                                    component="label"
                                    htmlFor="name"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Название проекта
                                </Typography>
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field }) => (
                                        <CustomTextField
                                            {...field}
                                            id="name"
                                            fullWidth
                                            placeholder="Введите название проекта"
                                            error={!!errors.name}
                                            helperText={errors.name?.message}
                                        />
                                    )}
                                />
                            </Stack>

                            <Stack gap={1}>
                                <Typography
                                    component="label"
                                    htmlFor="project_goal"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Цель проекта
                                </Typography>
                                <Controller
                                    name="project_goal"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl
                                            fullWidth
                                            error={!!errors.project_goal}
                                        >
                                            <Select
                                                {...field}
                                                id="project_goal"
                                                displayEmpty
                                                sx={{
                                                    borderRadius: '16px',
                                                    backgroundColor: '#fff',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'transparent',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#000000',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#000000',
                                                    },
                                                    '& .MuiSelect-select': {
                                                        color: field.value ? '#000000' : 'rgba(0, 0, 0, 0.6)',
                                                    },
                                                    '&.Mui-focused .MuiSelect-select': {
                                                        color: '#000000',
                                                    },
                                                }}
                                            >
                                                <MenuItem
                                                    value=""
                                                    disabled
                                                >
                                                    Выберите цель проекта
                                                </MenuItem>
                                                <MenuItem value="attract_clients">Привлечение клиентов</MenuItem>
                                                <MenuItem value="attract_telegram">
                                                    Привлечение подписчиков в Telegram
                                                </MenuItem>
                                            </Select>
                                            {errors.project_goal && (
                                                <FormHelperText>{errors.project_goal.message}</FormHelperText>
                                            )}
                                        </FormControl>
                                    )}
                                />
                            </Stack>

                            <Stack gap={1}>
                                <Typography
                                    component="label"
                                    htmlFor="audience_description"
                                    sx={{ fontWeight: 600 }}
                                >
                                    Опишите ваш продукт или услугу
                                </Typography>
                                <Controller
                                    name="audience_description"
                                    control={control}
                                    render={({ field }) => {
                                        const charCount = field.value.length;
                                        const isError = charCount > 150;

                                        return (
                                            <>
                                                <CustomTextField
                                                    {...field}
                                                    id="audience_description"
                                                    placeholder="Опишите максимально подробно для лучшего результата работы ТГХ"
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    error={isError}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            height: '134px',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        },
                                                    }}
                                                />

                                                <MaxChars
                                                    isError={isError}
                                                    charCount={charCount}
                                                    maxChars={150}
                                                />
                                            </>
                                        );
                                    }}
                                />
                            </Stack>

                            {!analysisResult && !isAnalyzing && (
                                <Box
                                    display="flex"
                                    alignItems="flex-end"
                                    flexGrow={1}
                                    pb={1}
                                    mt={1}
                                >
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        fullWidth
                                        type="submit"
                                        disabled={!isValid || isLoading}
                                        startIcon={!isLoading ? <AIIcon /> : undefined}
                                    >
                                        {isLoading ? <CircularProgress size={20} /> : 'Анализ ниши'}
                                    </Button>
                                </Box>
                            )}

                            {/* Анимация анализа ниши */}
                            {isAnalyzing && (
                                <Fade
                                    in={isAnalyzing}
                                    timeout={300}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: '24px',
                                            p: 3,
                                            mt: 3,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 3,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        animation: `${pulseAnimation} 2s ease-in-out infinite`,
                                                        borderRadius: '50%',
                                                        p: 2,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            animation: `${floatAnimation} 2s ease-in-out infinite`,
                                                        }}
                                                    >
                                                        <AIIcon
                                                            width={64}
                                                            height={64}
                                                        />
                                                    </Box>
                                                </Box>
                                                <CircularProgress
                                                    size={80}
                                                    thickness={2}
                                                    sx={{
                                                        position: 'absolute',
                                                        color: 'rgba(122, 48, 228, 0.3)',
                                                    }}
                                                />
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        fontWeight: 600,
                                                        background: 'linear-gradient(90deg, #7A30E4, #FF88C0)',
                                                        WebkitBackgroundClip: 'text',
                                                        WebkitTextFillColor: 'transparent',
                                                        backgroundClip: 'text',
                                                    }}
                                                >
                                                    ИИ анализирует вашу нишу
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        textAlign: 'center',
                                                        maxWidth: '400px',
                                                        color: 'rgba(0, 0, 0, 0.7)',
                                                    }}
                                                >
                                                    Генерируем ключевые слова, находим лиды и рекомендуем подборки
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'inline-block',
                                                            ml: 0.5,
                                                            '&::after': {
                                                                content: '"..."',
                                                                animation: `${dotsAnimation} 1.4s infinite`,
                                                            },
                                                        }}
                                                    />
                                                </Typography>
                                            </Box>

                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                sx={{ mt: 2 }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#7A30E4',
                                                        animation: `${dotsAnimation} 1.4s infinite`,
                                                        animationDelay: '0s',
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#FF88C0',
                                                        animation: `${dotsAnimation} 1.4s infinite`,
                                                        animationDelay: '0.2s',
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#9FA9BC',
                                                        animation: `${dotsAnimation} 1.4s infinite`,
                                                        animationDelay: '0.4s',
                                                    }}
                                                />
                                            </Stack>
                                        </Box>
                                    </Box>
                                </Fade>
                            )}
                        </Box>
                    </form>

                    {/* Показываем анимацию при повторном анализе (когда есть результаты, но идет анализ) */}
                    {analysisResult && isAnalyzing && (
                        <Box mt={4}>
                            <Fade
                                in={isAnalyzing}
                                timeout={300}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#F5F5F5',
                                        borderRadius: '24px',
                                        p: 3,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 3,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    animation: `${pulseAnimation} 2s ease-in-out infinite`,
                                                    borderRadius: '50%',
                                                    p: 2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        animation: `${floatAnimation} 2s ease-in-out infinite`,
                                                    }}
                                                >
                                                    <AIIcon
                                                        width={64}
                                                        height={64}
                                                    />
                                                </Box>
                                            </Box>
                                            <CircularProgress
                                                size={80}
                                                thickness={2}
                                                sx={{
                                                    position: 'absolute',
                                                    color: 'rgba(122, 48, 228, 0.3)',
                                                }}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    background: 'linear-gradient(90deg, #7A30E4, #FF88C0)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    backgroundClip: 'text',
                                                }}
                                            >
                                                ИИ анализирует вашу нишу
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    textAlign: 'center',
                                                    maxWidth: '400px',
                                                    color: 'rgba(0, 0, 0, 0.7)',
                                                }}
                                            >
                                                Перезапускаем анализ ниши
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-block',
                                                        ml: 0.5,
                                                        '&::after': {
                                                            content: '"..."',
                                                            animation: `${dotsAnimation} 1.4s infinite`,
                                                        },
                                                    }}
                                                />
                                            </Typography>
                                        </Box>

                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            sx={{ mt: 2 }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#7A30E4',
                                                    animation: `${dotsAnimation} 1.4s infinite`,
                                                    animationDelay: '0s',
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#FF88C0',
                                                    animation: `${dotsAnimation} 1.4s infinite`,
                                                    animationDelay: '0.2s',
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#9FA9BC',
                                                    animation: `${dotsAnimation} 1.4s infinite`,
                                                    animationDelay: '0.4s',
                                                }}
                                            />
                                        </Stack>
                                    </Box>
                                </Box>
                            </Fade>
                        </Box>
                    )}

                    {analysisResult && !isAnalyzing && (
                        <Fade
                            in={!!analysisResult && !isAnalyzing}
                            timeout={500}
                        >
                            <Box mt={4}>
                                <Box mb={3}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={analysisResult.category ? 1.5 : 0}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: 700 }}
                                        >
                                            Результаты ИИ-анализа ниши
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                if (activeProject?.id) {
                                                    const formValues = watch();
                                                    // Скрываем старые результаты перед началом нового анализа
                                                    setAnalysisResult(null);
                                                    analyzeNiche({
                                                        project_id: activeProject.id,
                                                        service_description: formValues.audience_description || '',
                                                        project_goal: formValues.project_goal || 'attract_clients',
                                                    });
                                                }
                                            }}
                                            disabled={isAnalyzing}
                                            startIcon={!isAnalyzing ? <AIIcon /> : undefined}
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: '8px',
                                            }}
                                        >
                                            {isAnalyzing ? <CircularProgress size={16} /> : 'Перезапустить анализ'}
                                        </Button>
                                    </Box>
                                    {analysisResult.category && (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                Категория:
                                            </Typography>
                                            <Chip
                                                label={analysisResult.category}
                                                icon={
                                                    <AIIcon
                                                        width={14}
                                                        height={14}
                                                    />
                                                }
                                                sx={{
                                                    backgroundColor: 'rgba(122, 48, 228, 0.1)',
                                                    color: '#000000',
                                                    fontWeight: 600,
                                                    border: '1px solid rgba(122, 48, 228, 0.2)',
                                                    '& .MuiChip-icon': {
                                                        color: '#7A30E4',
                                                    },
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {/* Ключевые слова */}
                                <Box mb={3}>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600, mb: 1.5 }}
                                    >
                                        Рекомендованные ключевые слова
                                    </Typography>
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={1}
                                    >
                                        {analysisResult.keywords.map((keyword, index) => (
                                            <Chip
                                                key={index}
                                                label={keyword}
                                                icon={
                                                    <AIIcon
                                                        width={14}
                                                        height={14}
                                                    />
                                                }
                                                sx={{
                                                    backgroundColor: 'rgba(122, 48, 228, 0.1)',
                                                    color: '#000000',
                                                    fontWeight: 600,
                                                    border: '1px solid rgba(122, 48, 228, 0.2)',
                                                    '& .MuiChip-icon': {
                                                        color: '#7A30E4',
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                {/* Рекомендованные подборки */}
                                {(analysisResult.recommended_collections.city_collections.length > 0 ||
                                    analysisResult.recommended_collections.general_collections.length > 0) && (
                                    <Box mb={3}>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 600, mb: 1.5 }}
                                        >
                                            Рекомендованные подборки
                                        </Typography>
                                        <List
                                            sx={{
                                                width: '100%',
                                                background: '#fff',
                                                border: '1px solid',
                                                borderColor: '#E3ECF3',
                                                display: 'block',
                                                borderRadius: '16px',
                                            }}
                                            disablePadding
                                        >
                                            {[
                                                ...analysisResult.recommended_collections.city_collections,
                                                ...analysisResult.recommended_collections.general_collections,
                                            ].map((collection, idx, arr) => (
                                                <ListItem
                                                    key={collection.id}
                                                    divider={idx !== arr.length - 1}
                                                >
                                                    <ListItemIcon>
                                                        <MapLayer />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1}
                                                            >
                                                                <Typography
                                                                    variant="body1"
                                                                    color="primary"
                                                                >
                                                                    {collection.name}
                                                                </Typography>
                                                                <Chip
                                                                    icon={
                                                                        <AIIcon
                                                                            width={14}
                                                                            height={14}
                                                                        />
                                                                    }
                                                                    label="ИИ рекомендует"
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: 'rgba(122, 48, 228, 0.1)',
                                                                        color: '#7A30E4',
                                                                        fontWeight: 600,
                                                                        border: '1px solid rgba(122, 48, 228, 0.2)',
                                                                        '& .MuiChip-icon': {
                                                                            color: '#7A30E4',
                                                                        },
                                                                    }}
                                                                />
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1}
                                                            >
                                                                {collection.city && (
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="primary"
                                                                        sx={{
                                                                            fontWeight: 500,
                                                                            backgroundColor: 'rgba(0, 136, 204, 0.1)',
                                                                            padding: '2px 8px',
                                                                            borderRadius: '12px',
                                                                            border: '1px solid rgba(0, 136, 204, 0.2)',
                                                                        }}
                                                                    >
                                                                        {collection.city}
                                                                    </Typography>
                                                                )}

                                                                {collection.chat_count !== undefined &&
                                                                    collection.chat_count > 0 && (
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gap={1}
                                                                        >
                                                                            <Peoples />
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="primary"
                                                                            >
                                                                                {getWordWithNumber(
                                                                                    collection.chat_count,
                                                                                    ['чат', 'чата', 'чатов']
                                                                                )}
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}

                                {/* Примеры лидов */}
                                {analysisResult.lead_examples.length > 0 && (
                                    <Box mb={3}>
                                        <Box
                                            mb={1.5}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'primary.main',
                                                }}
                                            >
                                                {(() => {
                                                    const count = analysisResult.lead_examples.length;
                                                    const currentValue = Math.abs(count) % 100;
                                                    const num = currentValue % 10;
                                                    const isFound =
                                                        (currentValue > 10 && currentValue < 20) || num === 0 || num > 4
                                                            ? 'Найдено'
                                                            : num === 1
                                                            ? 'Найден'
                                                            : 'Найдено';
                                                    return `${isFound} ${getWordWithNumber(count, [
                                                        'пример лида',
                                                        'примера лидов',
                                                        'примеров лидов',
                                                    ])}.`;
                                                })()}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#000000',
                                                    lineHeight: 1.6,
                                                    fontSize: '14px',
                                                }}
                                            >
                                                Выберите те, которые считаете наиболее релевантными — ИИ будет обучаться
                                                именно на них.
                                            </Typography>
                                        </Box>

                                        {/* Облако тегов лидов */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                mb: 2,
                                            }}
                                        >
                                            {analysisResult.lead_examples.map((lead) => {
                                                const isSelected = selectedLeads.has(lead.id);
                                                // Получаем иконку категории (может быть эмодзи в названии категории)
                                                const categoryIcon =
                                                    lead.category_icon ||
                                                    lead.category.match(/^[\u{1F300}-\u{1F9FF}]/u)?.[0] ||
                                                    '';
                                                // Удаляем эмодзи из начала названия категории (если есть)
                                                let categoryName = lead.category;
                                                if (lead.category && categoryIcon) {
                                                    // Если есть иконка, удаляем её из начала строки
                                                    categoryName = lead.category
                                                        .replace(
                                                            new RegExp(
                                                                `^${categoryIcon.replace(
                                                                    /[.*+?^${}()|[\]\\]/g,
                                                                    '\\$&'
                                                                )}\\s*`
                                                            ),
                                                            ''
                                                        )
                                                        .trim();
                                                }
                                                if (!categoryName) categoryName = lead.category;
                                                return (
                                                    <Box
                                                        key={lead.id}
                                                        onClick={() => {
                                                            setSelectedLeads((prev) => {
                                                                const newSet = new Set(prev);
                                                                if (newSet.has(lead.id)) {
                                                                    newSet.delete(lead.id);
                                                                } else {
                                                                    newSet.add(lead.id);
                                                                }
                                                                return newSet;
                                                            });
                                                        }}
                                                        sx={{
                                                            display: 'inline-flex',
                                                            flexDirection: 'column',
                                                            gap: 0.75,
                                                            padding: '14px 18px',
                                                            borderRadius: '16px',
                                                            backgroundColor: isSelected ? '#0044FF' : '#fff',
                                                            border: `1px solid ${
                                                                isSelected ? '#0044FF' : 'rgba(0, 0, 0, 0.12)'
                                                            }`,
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            maxWidth: '400px',
                                                            '&:hover': {
                                                                backgroundColor: isSelected
                                                                    ? '#0044FF'
                                                                    : 'rgba(0, 0, 0, 0.04)',
                                                                borderColor: isSelected
                                                                    ? '#0044FF'
                                                                    : 'rgba(0, 0, 0, 0.2)',
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={0.75}
                                                        >
                                                            {categoryIcon && (
                                                                <Typography
                                                                    component="span"
                                                                    sx={{
                                                                        fontSize: '18px',
                                                                        lineHeight: 1,
                                                                    }}
                                                                >
                                                                    {categoryIcon}
                                                                </Typography>
                                                            )}
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: isSelected ? '#fff' : '#000',
                                                                    fontSize: '14px',
                                                                }}
                                                            >
                                                                {categoryName}
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: isSelected
                                                                    ? 'rgba(255, 255, 255, 0.95)'
                                                                    : 'rgba(0, 0, 0, 0.8)',
                                                                fontSize: '14px',
                                                                lineHeight: 1.5,
                                                                wordWrap: 'break-word',
                                                                overflowWrap: 'break-word',
                                                            }}
                                                        >
                                                            {lead.text}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                )}

                                {/* Кнопка Сохранить */}
                                <Box
                                    display="flex"
                                    alignItems="flex-end"
                                    flexGrow={1}
                                    pb={1}
                                    mt={2}
                                >
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        fullWidth
                                        onClick={handleNext}
                                        disabled={!isValid || isSaving}
                                        startIcon={isSaving ? <CircularProgress size={20} /> : undefined}
                                    >
                                        {isSaving ? 'Сохранение...' : 'Сохранить'}
                                    </Button>
                                </Box>
                            </Box>
                        </Fade>
                    )}
                </Box>
            </LoadingWrapper>
        );
    };

    if (!isMobile) {
        return <DesktopLayout>{renderContent()}</DesktopLayout>;
    }

    return <LayoutPaper isWhite>{renderContent()}</LayoutPaper>;
}
